import "./editor.css";
import { initializeDiagramGallery } from "./diagramGallery";
import { initializeDocumentPdfDownloads } from "./documentPdfDownload";
import { initializeHtmxIntegration } from "./htmxIntegration";
import { initializeSettingsDialogs } from "./settingsDialog";
import { initializeCopyText } from "./copyText";
import { closePageLinksDrawer, initializePageLinksDrawer } from "./pageLinksDrawer";
import { initializePageLinkGraph } from "./pageLinkGraph";
import { initializeReaderTools } from "./readerTools";
import { initializeCharts } from "./charts";
import { initializeReadingTracker } from "./readingTracker";
import { initializeReadingLinks } from "./readingLinks";
import { initializeReadingGuide } from "./readingGuide";
import { initializeReadingHighlights } from "./readingHighlights";
import { initializeReaderNavigation } from "./readerNavigation";
import { initializeReview, type SavedDiscussion } from "./review";
import { passageText } from "./passageSelection";

initializeReaderTools();
let editorOpen = false;

document.addEventListener("click", async event => {
  const target = event.target as Element | null;
  const launch = target?.closest<HTMLButtonElement>("[data-qprins-editor-launch]");
  if (!launch) return;
  if (editorOpen) return;

  const hostId = launch.dataset.editorHost;
  const snapshotUrl = launch.dataset.snapshotUrl;
  const saveUrl = launch.dataset.saveUrl;
  if (!hostId || !snapshotUrl || !saveUrl) return;
  const host = document.getElementById(hostId);
  const staticDocument = document.querySelector<HTMLElement>("[data-qprins-static-document]");
  const nodeId = launch.dataset.editorNodeId;
  const staticBranch = nodeId ? document.getElementById(`node-${nodeId}`) : staticDocument;
  if (!host) return;

  closeDiscussionDrawer();
  closePageLinksDrawer(false);
  resetSelectionComment();
  launch.disabled = true;
  editorOpen = true;
  launch.textContent = "Loading editor…";
  try {
    const { mountEditor } = await import("./editor");
    if (nodeId && staticBranch) staticBranch.after(host);
    staticBranch?.setAttribute("hidden", "");
    host.removeAttribute("hidden");
    mountEditor(host, { snapshotUrl, saveUrl, nodeId, assetCatalogUrl: launch.dataset.assetCatalogUrl, sourceCatalogUrl: launch.dataset.sourceCatalogUrl });
    launch.hidden = true;
  } catch (error) {
    editorOpen = false;
    launch.hidden = false;
    launch.disabled = false;
    launch.textContent = "Edit";
    staticBranch?.removeAttribute("hidden");
    host.hidden = true;
    window.alert(error instanceof Error ? error.message : String(error));
  }
});

document.addEventListener("click", event => {
  const target = event.target as Element | null;
  const trigger = target?.closest<HTMLButtonElement>("[data-open-selection-comment], [data-open-passage-comment]");
  if (!trigger || editorOpen) return;

  const form = document.querySelector<HTMLFormElement>("[data-selection-comment-form]");
  if (!form) return;
  event.preventDefault();
  // Keep an open draft anchored to its original passage until the user submits or cancels it.
  if (!form.hidden) { form.querySelector<HTMLTextAreaElement>("textarea")?.focus({ preventScroll: true }); return; }

  const selectionNodeId = trigger.dataset.openSelectionComment;
  const nodeId = trigger.dataset.openPassageComment ?? selectionNodeId;
  let returnFocus: HTMLElement = trigger;
  if (nodeId) {
    const reader = document.querySelector<HTMLElement>("[data-qprins-static-document]");
    const passage = reader?.querySelector<HTMLElement>(`[data-comment-anchor='block'][data-node-id="${CSS.escape(nodeId)}"]`);
    if (!passage || !passage.getClientRects().length || !reader?.dataset.revisionId) return;
    const text = passageText(passage);
    const start = Number(trigger.dataset.selectionStart), end = Number(trigger.dataset.selectionEnd);
    if (selectionNodeId && (trigger.dataset.selectionRevision !== reader.dataset.revisionId
      || !Number.isInteger(start) || !Number.isInteger(end) || start < 0 || end <= start || end > text.length
      || text.slice(start, end) !== trigger.dataset.selectionText)) return;
    setInput(form, "revisionId", reader.dataset.revisionId);
    setInput(form, "anchorKind", selectionNodeId ? "TextRange" : "Block");
    setInput(form, "nodeId", nodeId);
    setInput(form, "startPosition", selectionNodeId ? String(start) : "");
    setInput(form, "endPosition", selectionNodeId ? String(end) : "");
    setCommentQuotation(form, selectionNodeId ? text.slice(start, end) : text.trim());
    markSelectionTarget(passage, "pending");
    returnFocus = passage.querySelector<HTMLElement>("[data-review-passage-button]") ?? passage;
  }

  const selectionTarget = document.querySelector<HTMLElement>("[data-selection-comment-target]");
  if (selectionTarget) selectionTarget.dataset.selectionCommentTarget = "composing";
  form.hidden = false;
  openDrawerContent(form, returnFocus, selectionNodeId ? "Comment on selected text" : "Comment on paragraph", "selection");
  form.querySelector<HTMLTextAreaElement>("textarea")?.focus({ preventScroll: true });
});

function setCommentQuotation(form: HTMLFormElement, quotation: string) {
  const target = form.querySelector<HTMLElement>("[data-selection-quotation]");
  if (target) target.textContent = quotation.length > 120 ? `${quotation.slice(0, 117)}…` : quotation;
}

document.addEventListener("click", event => {
  const target = event.target as Element | null;
  if (!target?.closest("[data-cancel-selection-comment]")) return;
  resetSelectionComment();
});

document.addEventListener("click", event => {
  const target = event.target as Element | null;
  const trigger = target?.closest<HTMLElement>("[data-annotation-indicator], [data-open-discussion]");
  if (!trigger) return;

  const threadId = trigger.dataset.annotationTarget
    ?? trigger.dataset.discussionTarget;
  const card = threadId ? document.getElementById(threadId) : null;
  if (!(card instanceof HTMLElement)) return;

  event.preventDefault();
  const drawer = requireDiscussionDrawer();
  if (activeDrawerContent?.element === card && drawer.open) {
    closeDiscussionDrawer();
    return;
  }

  openDiscussion(card, trigger, true);
});

function resetSelectionComment() {
  const form = document.querySelector<HTMLFormElement>("[data-selection-comment-form]");
  if (form && activeDrawerContent?.element === form) {
    closeDiscussionDrawer();
    return;
  }

  if (form) {
    form.hidden = true;
    form.reset();
  }
  clearSelectionTarget();
}

function clearSelectionTarget() {
  const form = document.querySelector<HTMLFormElement>("[data-selection-comment-form]");
  if (!form || form.hidden) markSelectionTarget(null);
}

function markSelectionTarget(target: HTMLElement | null, state?: "pending" | "composing") {
  document.querySelector<HTMLElement>("[data-selection-comment-target]")?.removeAttribute("data-selection-comment-target");
  if (target && state) target.dataset.selectionCommentTarget = state;
}

function initializeAnnotationIndicators(openFromHash = true) {
  document.querySelectorAll("[data-annotation-indicators]").forEach(element => element.remove());
  document.querySelectorAll<HTMLElement>("[data-has-discussions], [data-has-open-discussions]").forEach(element => {
    element.removeAttribute("data-has-discussions");
    element.removeAttribute("data-discussion-count");
    element.removeAttribute("data-has-open-discussions");
    element.removeAttribute("data-open-discussion-count");
  });

  const cards = [...document.querySelectorAll<HTMLElement>("[data-annotation-thread]")]
    .filter(card => Boolean(card.dataset.annotationNodeId));
  const cardsByNode = new Map<string, HTMLElement[]>();
  for (const card of cards) {
    const nodeId = card.dataset.annotationNodeId!;
    const existing = cardsByNode.get(nodeId);
    if (existing) existing.push(card);
    else cardsByNode.set(nodeId, [card]);
  }

  let referenceNumber = 1;
  for (const [nodeId, nodeCards] of cardsByNode) {
    const target = document.querySelector<HTMLElement>(`[data-qprins-static-document] [data-node-id="${CSS.escape(nodeId)}"]`);
    if (!target) continue;

    target.dataset.hasDiscussions = "true";
    target.dataset.discussionCount = String(nodeCards.length);

    const indicators = document.createElement("span");
    indicators.className = "annotation-indicators";
    indicators.dataset.annotationIndicators = "";
    indicators.dataset.annotationNodeId = nodeId;
    indicators.setAttribute("role", "group");
    indicators.setAttribute("aria-label", `${nodeCards.length} discussion${nodeCards.length === 1 ? "" : "s"} for this passage`);

    for (const card of nodeCards) {
      const reference = referenceNumber++;
      const indicator = document.createElement("button");
      indicator.type = "button";
      const excerpt = card.dataset.annotationExcerpt?.trim()
        || card.querySelector<HTMLElement>(".comment-message p")?.textContent?.trim()
        || card.querySelector("blockquote")?.textContent?.replace(/[“”]/g, "").trim()
        || "Open discussion";
      const excerptSummary = excerpt.length > 112 ? `${excerpt.slice(0, 109)}…` : excerpt;
      const state = card.dataset.annotationState ?? "open";
      const messageCount = Number.parseInt(card.dataset.annotationMessageCount ?? "", 10);
      const messageDescription = Number.isFinite(messageCount)
        ? `, ${messageCount} message${messageCount === 1 ? "" : "s"}`
        : "";
      indicator.className = "annotation-indicator annotation-inline";
      indicator.dataset.annotationIndicator = "";
      indicator.dataset.annotationTarget = card.id;
      indicator.dataset.annotationState = state;
      indicator.setAttribute("aria-controls", "discussion-drawer");
      indicator.setAttribute("aria-expanded", "false");
      indicator.setAttribute("aria-label", `Open discussion ${reference}${messageDescription}: ${excerptSummary}${state === "resolved" ? " (resolved)" : ""}`);
      indicator.title = indicator.getAttribute("aria-label")!;

      const referenceLabel = document.createElement("span");
      referenceLabel.className = "annotation-inline__reference";
      referenceLabel.setAttribute("aria-hidden", "true");
      referenceLabel.textContent = String(reference);
      const excerptLabel = document.createElement("span");
      excerptLabel.className = "annotation-inline__excerpt";
      excerptLabel.textContent = excerptSummary;
      const metaLabel = document.createElement("span");
      metaLabel.className = "annotation-inline__meta";
      metaLabel.textContent = [
        Number.isFinite(messageCount) ? `${messageCount} message${messageCount === 1 ? "" : "s"}` : null,
        state === "resolved" ? "resolved" : null
      ].filter(Boolean).join(" · ");
      indicator.append(referenceLabel, excerptLabel);
      if (metaLabel.textContent) indicator.append(metaLabel);
      indicators.append(indicator);

      const cardReference = card.querySelector<HTMLElement>("[data-annotation-reference]");
      if (cardReference) {
        cardReference.textContent = String(reference);
        cardReference.hidden = false;
        cardReference.setAttribute("aria-label", `Discussion ${reference}`);
      }
    }

    target.insertAdjacentElement("afterend", indicators);
  }

  if (openFromHash) openDiscussionFromHash();
}

async function refreshPageDiscussion(form: HTMLFormElement, result: SavedDiscussion): Promise<void> {
  const reader = document.querySelector<HTMLElement>("[data-qprins-static-document]");
  const footer = document.querySelector<HTMLElement>("[data-page-discussions]");
  if (!reader?.dataset.revisionId || !footer) throw new Error("The displayed page is unavailable.");
  const revisionId = reader.dataset.revisionId;
  const url = new URL(location.href);
  url.hash = ""; url.searchParams.set("revision", revisionId); url.searchParams.delete("edit");
  const response = await fetch(url, { credentials: "same-origin", cache: "no-store", headers: { Accept: "text/html" } });
  if (!response.ok) throw new Error("The discussion could not be refreshed.");
  const rendered = new DOMParser().parseFromString(await response.text(), "text/html");
  const id = `discussion-thread-${result.threadId}`;
  const incoming = rendered.getElementById(id);
  if (!incoming?.matches("[data-annotation-thread]") || !reader.isConnected || reader.dataset.revisionId !== revisionId)
    throw new Error("The saved discussion is unavailable for the displayed revision.");
  const card = document.importNode(incoming, true);
  const existing = document.getElementById(id);
  const returnFocus = activeDrawerContent?.trigger ?? form.querySelector<HTMLElement>("button[type=submit]");
  if (existing) {
    existing.replaceWith(card);
    if (activeDrawerContent?.element === existing) activeDrawerContent.element = card;
  } else {
    let list = footer.querySelector<HTMLElement>(".discussion-thread-index");
    if (!list) { list = document.createElement("div"); list.className = "thread-list discussion-thread-index"; footer.append(list); }
    list.append(card);
  }
  footer.querySelector(".discussion-empty-state")?.remove();
  for (const selector of [".discussion-summary-bar", ".discussion-footer__header > p"]) {
    const old = document.querySelector(selector), replacement = rendered.querySelector(selector);
    if (old && replacement) old.replaceChildren(...[...replacement.childNodes].map(node => document.importNode(node, true)));
  }
  form.reset();
  initializeAnnotationIndicators(false);
  openDiscussion(card, returnFocus, false);
  history.replaceState(null, "", `${location.pathname}${location.search}#${result.messageId ? `comment-${result.messageId}` : id}`);
  // Only the drawer scrolls to the new reply. The article and reading guide stay in place.
  const body = getDiscussionDrawer()?.querySelector<HTMLElement>("[data-discussion-drawer-content]");
  const message = result.messageId ? card.querySelector<HTMLElement>(`#comment-${CSS.escape(result.messageId)}`) : undefined;
  if (body && message) body.scrollTop += message.getBoundingClientRect().top - body.getBoundingClientRect().top;
}

type DrawerContentKind = "thread" | "selection";

interface ActiveDrawerContent {
  element: HTMLElement;
  placeholder: Comment;
  trigger: HTMLElement | null;
  kind: DrawerContentKind;
}

let activeDrawerContent: ActiveDrawerContent | null = null;

function getDiscussionDrawer(): HTMLDialogElement | null {
  return document.querySelector<HTMLDialogElement>("dialog[data-discussion-drawer]");
}

function requireDiscussionDrawer(): HTMLDialogElement {
  const drawer = getDiscussionDrawer();
  if (!drawer) throw new Error("The discussion drawer is missing from the page.");
  return drawer;
}

function getDrawerContent(drawer: HTMLDialogElement): HTMLElement | null {
  return drawer.querySelector<HTMLElement>("[data-discussion-drawer-content]");
}

function openDrawerContent(
  element: HTMLElement,
  trigger: HTMLElement | null,
  title: string,
  kind: DrawerContentKind) {
  const drawer = requireDiscussionDrawer();
  const content = getDrawerContent(drawer);
  if (!content) throw new Error("The discussion drawer content host is missing from the page.");

  closePageLinksDrawer(false);

  if (activeDrawerContent?.element === element && drawer.open) return;
  if (activeDrawerContent) restoreDrawerContent(false);

  const placeholder = document.createComment(`qprins-${kind}-drawer-placeholder`);
  element.before(placeholder);
  content.append(element);
  activeDrawerContent = { element, placeholder, trigger, kind };
  drawer.dataset.discussionDrawerKind = kind;

  const titleTarget = drawer.querySelector<HTMLElement>("[data-discussion-drawer-title]");
  if (titleTarget) titleTarget.textContent = title;
  if (!drawer.open) drawer.show();
}

function openDiscussion(card: HTMLElement, trigger: HTMLElement | null, updateHash: boolean) {
  const reference = card.querySelector<HTMLElement>("[data-annotation-reference]")?.textContent?.trim();
  const title = card.dataset.discussionTitle
    ?? (reference ? `Discussion ${reference}` : "Discussion");
  openDrawerContent(card, trigger, title, "thread");

  activateAnnotation(card);
  if (updateHash && window.location.hash !== `#${card.id}`) {
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${window.location.search}#${card.id}`);
  }
  card.focus({ preventScroll: true });
}

function activateAnnotation(card: HTMLElement) {
  clearActiveAnnotation();

  card.dataset.annotationActive = "true";
  const escapedId = CSS.escape(card.id);
  document.querySelectorAll<HTMLElement>(
    `[data-annotation-target="${escapedId}"], [data-discussion-target="${escapedId}"], [aria-controls="${escapedId}"]`)
    .forEach(control => {
      control.dataset.annotationActive = "true";
      control.setAttribute("aria-expanded", "true");
    });

  const nodeId = card.dataset.annotationNodeId;
  const target = nodeId
    ? document.querySelector<HTMLElement>(`[data-qprins-static-document] [data-node-id="${CSS.escape(nodeId)}"]`)
    : null;
  if (target) target.dataset.annotationActive = "true";
}

function clearActiveAnnotation() {
  document.querySelectorAll<HTMLElement>("[data-annotation-active]").forEach(element => {
    element.removeAttribute("data-annotation-active");
    if (element.matches("[data-annotation-indicator], [data-open-discussion]")) {
      element.setAttribute("aria-expanded", "false");
    }
  });
}

function restoreDrawerContent(returnFocus: boolean) {
  const active = activeDrawerContent;
  if (!active) return;
  activeDrawerContent = null;

  const { element, placeholder, trigger, kind } = active;
  if (placeholder.parentNode) placeholder.parentNode.insertBefore(element, placeholder);
  placeholder.remove();
  if (kind === "selection" && element instanceof HTMLFormElement) {
    element.hidden = true;
    element.reset();
    clearSelectionTarget();
  } else {
    clearActiveAnnotation();
    if (window.location.hash === `#${element.id}`) {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
    }
  }

  const drawer = getDiscussionDrawer();
  if (drawer) delete drawer.dataset.discussionDrawerKind;

  if (returnFocus && trigger?.isConnected && trigger.getClientRects().length > 0) {
    trigger.focus({ preventScroll: true });
  }
}

function closeDiscussionDrawer() {
  const drawer = getDiscussionDrawer();
  if (drawer?.open) drawer.close();
  else restoreDrawerContent(true);
}

function initializeDiscussionDrawer() {
  const drawer = getDiscussionDrawer();
  if (!drawer) return;

  drawer.addEventListener("close", () => restoreDrawerContent(true));
  document.addEventListener("keydown", event => {
    if (event.key === "Escape" && !event.defaultPrevented && drawer.open) closeDiscussionDrawer();
  });
  drawer.addEventListener("click", event => {
    const target = event.target as Element | null;
    if (target === drawer || target?.closest("[data-close-discussion-drawer]")) {
      closeDiscussionDrawer();
    }
  });
}

function openDiscussionFromHash() {
  const id = currentHashTargetId();
  if (!id) return;
  const card = document.getElementById(id);
  if (!(card instanceof HTMLElement) || !card.matches("[data-annotation-thread]")) return;

  const trigger = document.querySelector<HTMLElement>(`[data-annotation-target="${CSS.escape(id)}"]`)
    ?? document.querySelector<HTMLElement>(`[data-discussion-target="${CSS.escape(id)}"]`);
  openDiscussion(card, trigger, false);
}

function currentHashTargetId() {
  return window.location.hash.length > 1
    ? window.location.hash.slice(1)
    : null;
}

function setInput(form: HTMLFormElement, name: string, value: string) {
  const input = form.elements.namedItem(name);
  if (input instanceof HTMLInputElement) input.value = value;
}

window.addEventListener("resize", () => {
  clearSelectionTarget();
});
window.addEventListener("scroll", () => {
  clearSelectionTarget();
}, { passive: true });
window.addEventListener("hashchange", () => {
  const hashTargetId = currentHashTargetId();
  const hashTarget = hashTargetId ? document.getElementById(hashTargetId) : null;
  if (hashTarget instanceof HTMLElement && hashTarget.matches("[data-annotation-thread]")) {
    openDiscussionFromHash();
  } else if (activeDrawerContent?.kind === "thread") {
    closeDiscussionDrawer();
  }
});

requestAnimationFrame(() => {
  initializeReadingGuide();
  initializeReadingHighlights();
  initializeReaderNavigation();
  initializeReadingTracker();
  initializeReadingLinks();
  initializeReview({ onPageDiscussionSaved: refreshPageDiscussion });
  initializeCharts();
  initializeCopyText();
  initializeHtmxIntegration();
  initializeDocumentPdfDownloads();
  initializeSettingsDialogs();
  initializePageLinkGraph();
  initializePageLinksDrawer({
    beforeOpen: () => {
      closeDiscussionDrawer();
      resetSelectionComment();
    }
  });
  initializeDiagramGallery({
    beforeOpen: () => {
      closeDiscussionDrawer();
      closePageLinksDrawer(false);
      resetSelectionComment();
    }
  });
  initializeDiscussionDrawer();
  initializeAnnotationIndicators();
  document.querySelector<HTMLButtonElement>('[data-qprins-editor-launch][data-open-immediately="true"]')?.click();
});
